import React, { useMemo, useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { trackContentViewPdf, trackContentViewByType, trackContentView } from 'tw-oi-core/services/analytics'

import { CONTENT_TYPE, ROUTE } from '../config'
import { getStartingBreadCrumb, isPdf } from '../extras/utils'
import {
  buildSearchOriginBreadcrumbs,
  getBrowseSearchOrigin,
} from '../extras/browseSearchOrigin'
import BrowseGuideHeader from '../components/BrowseGuideHeader'
import BreadCrumb from '../components/BreadCrumb'
import StickyBar from '../components/StickyBar'
import Media from '../components/Media'
import { ScrollSink } from '../components/MotionScroll'
import { bindActionCreators } from 'redux'
import * as UserActions from 'tw-oi-core/actions/UserActions'
import { isOffline } from '../services/offlineDetect'
import { getCachedPdfBlobUrl, getCachedPdfData } from '../services/offlineStorage'
import { findOfflineComponentManualPdf } from '../services/offlineComponentManualPdf'
import { findOfflineVehicleForRoute, resolveBrandRouteFromBaseRoute } from '../extras/loadOfflineGuideVehicle'

import '../styles/PdfViewer.scss'
import { BRAND_SPECIFIC_TAG } from './BrandSpecificInformation'
import PdfViewer from '../components/PdfViewer'
import Loader from '../components/Loader'

const PdfViever = ({
  baseRoute,
  match,
  location,
  publications,
  UserActions,
  currentYear,
  currentModel,
  currentBrand,
  currentProductType,
  currentProductSubtype,
  currentId,
}) => {
  const [pdfSource, setPdfSource] = useState(null)
  const [resolvedPdf, setResolvedPdf] = useState(null)
  const downloadBlobUrlRef = useRef(null)

  const searchOrigin = getBrowseSearchOrigin(location)

  const breadcrumbs = useMemo(() => {
    const doc = resolvedPdf

    if (searchOrigin) {
      return buildSearchOriginBreadcrumbs(searchOrigin, doc?.title || 'PDF')
    }

    const items = [
      { title: getStartingBreadCrumb(match.params.referrer), link: `${baseRoute}${match.params.referrer}` },
    ]

    if (match.params.referrer === ROUTE.BROWSE && !match.params.brandSpecific) {
      items.push({ title: 'Component Manuals', link: `${baseRoute}${ROUTE.BROWSE}${ROUTE.PDFS}` })
    }

    if (match.params.brandSpecific) {
      items.push({ title: BRAND_SPECIFIC_TAG, link: `${baseRoute}${ROUTE.BROWSE}${ROUTE.BRAND_SPECIFIC}` })
    }

    if (match.params.pseudoFolderName) {
      items.push({
        title: match.params.pseudoFolderName,
        link: `${baseRoute}${ROUTE.BROWSE}${ROUTE.PDFS}/${match.params.pseudoFolderName}`,
      })
    }

    if (doc) {
      items.push({ title: doc.title, link: '' })
    }

    return items
  }, [match, baseRoute, resolvedPdf, searchOrigin])

  useEffect(() => {
    let cancelled = false

    const resolvePdf = async() => {
      let doc = publications.filter(isPdf).find(pub => pub.resourceKey === match.params.pdfResourceKey)

      if (!doc && isOffline()) {
        const brandRoute = resolveBrandRouteFromBaseRoute(baseRoute)
        const record = await findOfflineVehicleForRoute({
          baseRoute,
          brandRoute,
          currentBrand,
        })
        const vehicleId = record ? String(record.vehicleId) : (currentId ? String(currentId) : null)

        if (vehicleId) {
          doc = await findOfflineComponentManualPdf(
            vehicleId,
            match.params.pdfResourceKey,
            publications
          )
        }
      }

      if (!doc?.bodyPdf?.url) {
        if (!cancelled) {
          setResolvedPdf(null)
          setPdfSource(null)
        }
        return
      }

      if (!cancelled) {
        setResolvedPdf(doc)
      }

      if (isOffline()) {
        const pdfData = await getCachedPdfData(doc.bodyPdf.url)
        const downloadUrl = pdfData
          ? URL.createObjectURL(new Blob([pdfData], { type: 'application/pdf' }))
          : await getCachedPdfBlobUrl(doc.bodyPdf.url)

        if (cancelled) {
          if (downloadUrl.startsWith('blob:')) {
            URL.revokeObjectURL(downloadUrl)
          }
          return
        }

        if (downloadBlobUrlRef.current) {
          URL.revokeObjectURL(downloadBlobUrlRef.current)
        }
        if (downloadUrl.startsWith('blob:')) {
          downloadBlobUrlRef.current = downloadUrl
        } else {
          downloadBlobUrlRef.current = null
        }

        setPdfSource({
          pdfData: pdfData || null,
          pdfUrl: pdfData ? null : downloadUrl,
          downloadUrl,
        })
        return
      }

      if (!cancelled) {
        setPdfSource({
          pdfData: null,
          pdfUrl: doc.bodyPdf.url,
          downloadUrl: doc.bodyPdf.url,
        })
      }
    }

    resolvePdf()

    return () => {
      cancelled = true
    }
  }, [publications, baseRoute, currentBrand, currentId, match.params.pdfResourceKey])

  useEffect(() => () => {
    if (downloadBlobUrlRef.current) {
      URL.revokeObjectURL(downloadBlobUrlRef.current)
    }
  }, [])

  useEffect(() => {
    if (!resolvedPdf?.resourceKey || !resolvedPdf?.title) return
    if (!currentYear || !currentModel) return

    trackContentView(resolvedPdf.title)
    trackContentViewPdf(resolvedPdf.title)
    trackContentViewByType(resolvedPdf.title, CONTENT_TYPE.PDF)
    UserActions.addRecent(
      currentYear,
      currentModel,
      resolvedPdf.resourceKey,
      currentBrand,
      currentProductType,
      currentProductSubtype
    )
  }, [resolvedPdf?.resourceKey, resolvedPdf?.title, currentYear, currentModel, currentBrand, currentProductType, currentProductSubtype, UserActions])

  if (!resolvedPdf && !pdfSource) {
    return <Loader className='inverse' />
  }

  const title = resolvedPdf?.title || 'PDF'
  const viewerProps = pdfSource
    ? {
      pdfUrl: pdfSource.pdfUrl,
      pdfData: pdfSource.pdfData,
      downloadUrl: pdfSource.downloadUrl,
    }
    : null

  return (
    <div className='PdfViewer Topics one'>
      <BrowseGuideHeader />

      <Media type='mobile'>
        <div className='PdfViewer-mobile-breadcrumb'>
          <BreadCrumb items={breadcrumbs} />
        </div>
      </Media>

      <div className='screen-content'>
        <Media type='desktop'>
          <StickyBar className='sticky-topics-bar'>
            <div className='topics-bar'>
              <BreadCrumb items={breadcrumbs} />
            </div>
          </StickyBar>

          <div className='screen-container'>
            <div className='content-container'>
              <div className='content-container-wrapper'>
                {viewerProps ? <PdfViewer {...viewerProps} title={title} /> : <Loader className='inverse' />}
              </div>
            </div>
          </div>
        </Media>

        <Media type='mobile'>
          <ScrollSink scrollTop={0} resetScroll={true}>
            <div className='screen-container'>
              {viewerProps ? <PdfViewer {...viewerProps} title={title} /> : <Loader className='inverse' />}
            </div>
          </ScrollSink>
        </Media>
      </div>
    </div>
  )
}

PdfViever.propTypes = {
  baseRoute: PropTypes.string,
  currentYear: PropTypes.string,
  currentModel: PropTypes.string,
  currentBrand: PropTypes.string,
  currentProductType: PropTypes.string,
  currentProductSubtype: PropTypes.string,
  currentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  match: PropTypes.object,
  location: PropTypes.shape({
    state: PropTypes.object,
  }),
  publications: PropTypes.array,
  contents: PropTypes.array,
  UserActions: PropTypes.shape({
    addRecent: PropTypes.func.isRequired,
  }).isRequired,
}

const mapStateToProps = ({ vehicle, contents }) => {
  const { currentYear, currentModel, currentBrand, currentProductType, currentProductSubtype, currentId } = vehicle
  const { publications } = contents
  const pubs = publications
    ? (typeof publications.toJS === 'function' ? publications.toJS() : publications)
    : []

  return {
    currentYear,
    currentModel,
    currentBrand,
    currentProductType,
    currentProductSubtype,
    currentId,
    publications: pubs,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    UserActions: bindActionCreators(UserActions, dispatch),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PdfViever)
